import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Sparkles, Phone, Mail } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';
import { API } from '@/lib/api';

const SEEN_KEY = 'cfsc_exit_seen_v1';
const SKIP_PATHS = ['/contact', '/reservation', '/admin'];
const DESKTOP_ONLY = true; // exit-intent doesn't really work on touch devices

/**
 * Exit-intent popup — catches visitors about to leave the site.
 * Shown only once per session (sessionStorage), on desktop, when mouse
 * leaves the top edge of the viewport (back-to-browser-chrome gesture).
 * Mini form: prénom + téléphone + (optionnel) email. POST /api/contact.
 */
export function ExitIntentModal() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const armedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (SKIP_PATHS.some((p) => pathname.startsWith(p))) return;
    if (sessionStorage.getItem(SEEN_KEY)) return;
    if (DESKTOP_ONLY && window.matchMedia('(pointer: coarse)').matches) return;

    const armTimer = setTimeout(() => { armedRef.current = true; }, 10000); // arm after 10s
    const onLeave = (e) => {
      if (!armedRef.current) return;
      if (e.clientY <= 0) {
        setOpen(true);
        sessionStorage.setItem(SEEN_KEY, '1');
        trackEvent('exit_intent_shown');
        document.removeEventListener('mouseout', onLeave);
      }
    };
    document.addEventListener('mouseout', onLeave);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseout', onLeave);
    };
  }, [pathname]);

  const close = () => {
    setOpen(false);
    trackEvent('exit_intent_dismissed');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Prénom et téléphone requis');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim() || `no-email+${Date.now()}@cfsc.fr`,
        phone: form.phone.trim(),
        message: '[Exit-intent] Demande d\'essai gratuit depuis la popup. Merci de rappeler au plus vite.',
      });
      trackEvent('contact_form_submit', { form: 'exit_intent' });
      setSubmitted(true);
    } catch {
      toast.error("Erreur, essaie via la page contact ou appelle-nous au 06 84 28 40 87");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
          data-testid="exit-intent-overlay"
        >
          <motion.div
            initial={{ y: 40, scale: 0.95, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 40, scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="exit-intent-modal"
          >
            {/* Close */}
            <button
              onClick={close}
              data-testid="exit-intent-close"
              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/5 hover:bg-black/10 transition-colors"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Header */}
            <div className="relative bg-gradient-to-br from-primary via-primary-dark to-dark p-7 text-white">
              <div className="absolute -top-16 -right-16 w-60 h-60 bg-primary-glow/20 rounded-full blur-[80px] pointer-events-none" />
              <div className="relative z-10">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.25em] uppercase text-primary-glow mb-3">
                  <Sparkles className="h-3 w-3" /> Attends — on a mieux pour toi
                </span>
                <h3 className="font-heading text-2xl sm:text-3xl tracking-tight leading-tight mb-2">
                  Ta séance d'essai est{' '}
                  <span className="italic font-display">offerte</span>.
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Avant de partir, laisse-nous ton prénom et ton téléphone : on te rappelle dans les 24h pour caler ton cours gratuit.
                </p>
              </div>
            </div>

            {/* Form or thank-you */}
            <div className="p-6 sm:p-7">
              {!submitted ? (
                <form onSubmit={submit} className="space-y-3" data-testid="exit-intent-form">
                  <input
                    type="text"
                    required
                    placeholder="Ton prénom *"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="exit-intent-input-name"
                    className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 transition-all"
                  />
                  <input
                    type="tel"
                    required
                    placeholder="Ton téléphone *"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="exit-intent-input-phone"
                    className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 transition-all"
                  />
                  <input
                    type="email"
                    placeholder="Email (optionnel)"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="exit-intent-input-email"
                    className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 transition-all"
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="exit-intent-submit"
                    className="w-full btn-shine bg-primary hover:bg-primary-dark text-white font-heading tracking-wider text-sm py-6 rounded-xl glow-primary disabled:opacity-60"
                  >
                    {loading ? 'ENVOI…' : 'JE RÉSERVE MON ESSAI GRATUIT'}
                  </Button>
                  <p className="text-center text-[11px] text-muted-foreground pt-1">
                    Sans engagement · Pas de spam · RGPD conforme
                  </p>
                </form>
              ) : (
                <div className="text-center py-4" data-testid="exit-intent-success">
                  <div className="w-14 h-14 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <h4 className="font-heading text-xl mb-2">Top, message reçu !</h4>
                  <p className="text-muted-foreground text-sm mb-5">
                    On te rappelle dans les 24h au numéro que tu nous as donné.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs">
                    <a
                      href="tel:+33684284087"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border hover:border-primary hover:text-primary transition-colors"
                    >
                      <Phone className="h-3.5 w-3.5" /> 06 84 28 40 87
                    </a>
                    <a
                      href="mailto:crossfit.saintchamond@gmail.com"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border hover:border-primary hover:text-primary transition-colors"
                    >
                      <Mail className="h-3.5 w-3.5" /> Écrire un email
                    </a>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
